import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Gamepad2, ThumbsUp, Clock, CheckCircle, Sparkles } from 'lucide-react';
import { votesApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { LoginModal } from '../components/auth/LoginModal';
import { useTranslation } from 'react-i18next';

interface GameInfo {
  id: string;
  name: string;
  image: string;
  status: 'supported' | 'upcoming';
}

const ALL_GAMES: GameInfo[] = [
  { id: 'minecraft', name: 'Minecraft', image: 'https://cdn-icons-png.flaticon.com/512/5969/5969059.png', status: 'supported' },
  { id: 'valheim', name: 'Valheim', image: 'https://cdn2.steamgriddb.com/icon/0a09c8844ba8f527a1f39b7c2509658c/32/256x256.png', status: 'supported' },
  { id: 'terraria', name: 'Terraria', image: 'https://cdn2.steamgriddb.com/icon/4265764ad100e57fc3bcd0e357e1a5de/32/256x256.png', status: 'upcoming' },
  { id: 'rust', name: 'Rust', image: 'https://cdn2.steamgriddb.com/icon/38b85e8fabda6fb95bddf9bd8cb4906a/32/256x256.png', status: 'upcoming' },
  { id: 'ark', name: 'ARK: Survival Evolved', image: 'https://cdn2.steamgriddb.com/icon/2b3165e2d7b6afbf9b83ea3be50eb2a3/32/256x256.png', status: 'upcoming' },
  { id: 'csgo', name: 'CS2', image: 'https://cdn2.steamgriddb.com/icon/e2c42185a0459ef642f9e36cce3e20a6/32/256x256.png', status: 'upcoming' },
  { id: 'palworld', name: 'Palworld', image: 'https://cdn2.steamgriddb.com/icon/79f99a7e22cd26b3b868a410e80611af/32/256x256.png', status: 'upcoming' },
  { id: 'satisfactory', name: 'Satisfactory', image: 'https://cdn2.steamgriddb.com/icon/28cc46fb48ce4c3f6e72e9dd6dcd04e5/32/256x256.png', status: 'upcoming' },
  { id: 'zomboid', name: 'Project Zomboid', image: 'https://cdn2.steamgriddb.com/icon/90eb89a3bfaa8dc5d61e55e67a05b907/32/256x256.png', status: 'upcoming' },
  { id: 'fivem', name: 'FiveM (GTA V)', image: 'https://cdn2.steamgriddb.com/icon/65f1e8e17e8495dc4ebd2579c8635cef/32/256x256.png', status: 'upcoming' },
];

export function GamesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const supportedGames = ALL_GAMES.filter((g) => g.status === 'supported');
  const upcomingGames = ALL_GAMES.filter((g) => g.status === 'upcoming');

  // Fetch vote counts (public)
  const { data: votesData } = useQuery({
    queryKey: ['game-votes'],
    queryFn: votesApi.getVotes,
  });

  // Fetch user's today votes (only if logged in)
  const { data: myVotesData } = useQuery({
    queryKey: ['my-votes'],
    queryFn: votesApi.getMyVotes,
    enabled: !!user,
  });

  const voteMutation = useMutation({
    mutationFn: (game: string) => votesApi.vote(game),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['game-votes'] });
      queryClient.invalidateQueries({ queryKey: ['my-votes'] });
    },
  });

  const voteCounts = votesData?.votes || {};
  const votedToday = new Set(myVotesData?.votedGames || []);

  const handleVote = (gameId: string) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (votedToday.has(gameId)) return;
    voteMutation.mutate(gameId);
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-900 via-dark-950 to-dark-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyber-500/10 via-transparent to-transparent" />
        <div className="container mx-auto px-4 relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyber-500/10 border border-cyber-500/20 rounded-full text-cyber-400 text-sm mb-6">
            <Gamepad2 className="w-4 h-4" />
            {t('games.badge')}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
            {t('games.title')}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('games.subtitle')}
          </p>
        </div>
      </section>

      {/* Supported Games */}
      <section className="py-16 border-t border-dark-700">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <CheckCircle className="w-6 h-6 text-neon-400" />
            <h2 className="text-2xl font-bold text-gray-100">{t('games.supportedTitle')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {supportedGames.map((game) => (
              <div
                key={game.id}
                className="card p-6 border-neon-500/20 hover:border-neon-500/40 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-dark-700 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={game.image}
                      alt={game.name}
                      className="w-10 h-10 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).parentElement!.innerHTML = `<div class="w-10 h-10 bg-neon-500/20 rounded-lg flex items-center justify-center"><span class="text-neon-400 text-lg font-bold">${game.name[0]}</span></div>`;
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-100">{game.name}</h3>
                    <span className="inline-flex items-center gap-1 text-xs text-neon-400 bg-neon-500/10 px-2 py-0.5 rounded-full mt-1">
                      <CheckCircle className="w-3 h-3" />
                      {t('games.available')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Games + Voting */}
      <section className="py-16 bg-dark-900/50 border-t border-dark-700">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-6 h-6 text-cyber-400" />
            <h2 className="text-2xl font-bold text-gray-100">{t('games.upcomingTitle')}</h2>
          </div>
          <p className="text-gray-400 mb-8 max-w-2xl">
            {t('games.upcomingSubtitle')}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {upcomingGames.map((game) => {
              const count = voteCounts[game.id] || 0;
              const hasVoted = votedToday.has(game.id);

              return (
                <div
                  key={game.id}
                  className="card p-5 hover:border-dark-500 transition-all"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-dark-700 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                      <img
                        src={game.image}
                        alt={game.name}
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerHTML = `<div class="w-8 h-8 bg-cyber-500/20 rounded-lg flex items-center justify-center"><span class="text-cyber-400 text-sm font-bold">${game.name[0]}</span></div>`;
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-100">{game.name}</h3>
                      <span className="text-xs text-gray-500">{t('games.comingSoon')}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">
                      <span className="text-gray-200 font-medium">{count}</span> {t('games.votes')}
                    </span>
                    <button
                      onClick={() => handleVote(game.id)}
                      disabled={hasVoted || voteMutation.isPending}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        hasVoted
                          ? 'bg-neon-500/20 text-neon-400 cursor-default'
                          : 'bg-dark-600 text-gray-300 hover:bg-cyber-500/20 hover:text-cyber-400'
                      }`}
                    >
                      <ThumbsUp className={`w-4 h-4 ${hasVoted ? 'fill-neon-400' : ''}`} />
                      {hasVoted ? t('games.voted') : t('games.vote')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vote info */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 text-sm text-gray-500">
              <Sparkles className="w-4 h-4" />
              {t('games.voteInfo')}
            </div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            setShowLoginModal(false);
            queryClient.invalidateQueries({ queryKey: ['my-votes'] });
          }}
        />
      )}
    </div>
  );
}
